/**
 * The Authentication wrapper for all Fabric Apps. You are encouraged to read
 * the comments to understand what's going on (it's a _little_ complicated but
 * not too much.)
 *
 * Happy Path TLDR:
 *
 * - If you're a human and you JUST logged in, use Session Codes for new JWTs
 * - If you are a human and you open a Fabric App later (after you logged in)
 *   use cached Refresh Tokens for new JWTs
 * - If you want to automate tests, inject and use Identity Token
 */

import React from "react";
import jwtDecode from "jwt-decode";

import { GKLayoutShell } from "@granular/gds-ux-components";

import { useManageEnablingFreshData } from "../hooks";
import {
  useSetAuthentication,
  useMaybeUserId,
  useMaybeAuthentication,
  useMaybeUser,
  useMaybeLocale,
  useMaybeFabricFeatureFlag,
  useManageLocaleHeader,
  useFabricFeatureFlagsStatus,
  useMaybePreOperations,
} from "../next/hooks";
import Loading from "./Loading";
import BigGiantOops from "./Error";
import AddOperation from "./AddOperation";
import { segmentService } from "../legacy/segment/SegmentService";
import { Authentication, AuthSuccessSpec, JWT } from "../models/authentication";
import { getSSOUrl } from "../helpers/url";
import authService from "../services/auth";
import { State } from "../services/query";
import { ShellProps } from "./FabricShell";
import catalog from "@granular/fabric-catalog";

/**
 * Some constants.
 *
 * We use LocalStorage to cache the Refresh and ID Tokens. That's what the `LS`
 * in `LSRefreshKey` means. We also set the interval with which we get a fresh
 * set of the Refresh and Other Tokens. It's approximately 50 minutes. Why
 * approximately? Because JavaScript <3
 */
export const LSRefreshKey = "granular-refresh-token";
export const LSIdentityKey = "granular-identity-token";
export const refreshInterval = 3000000;

/**
 *
 * --- Authentication Mechanisms 🥸 ---
 *
 */

/**
 * Call the Auth Service with the value of the `?code` param if it exists. This
 * should normally be passed to the app by SSO on login and subsequent
 * redirection. If it exists, use it to procure new tokens.
 */
export const trySessionCode = async (
  authApiFlag?: boolean,
): Promise<AuthSuccessSpec | false> => {
  const code = new URLSearchParams(window.location.search).get("code");

  if (code) {
    try {
      if (authApiFlag) {
        const { data } = await catalog.auth.fetchTokens({
          code,
          authRequestConfig: {},
        });

        return data;
      }

      return await authService.fetchTokens(code);
    } catch (error) {
      return false;
    }
  }

  return false;
};

/**
 * If a LocalStorage token named `granular-refresh-token` exists, try to use
 * that to call the Auth Service to fetch new tokens.
 */
export const tryLocalRefreshToken = async (
  authApiFlag?: boolean,
): Promise<AuthSuccessSpec | false> => {
  const LSRefreshToken = localStorage.getItem(LSRefreshKey);

  if (LSRefreshToken) {
    try {
      if (authApiFlag) {
        const { data } = await catalog.auth.refreshTokens({
          token: LSRefreshToken,
          authRequestConfig: {},
        });

        return data;
      }

      return await authService.refreshToken(LSRefreshToken);
    } catch (error) {
      localStorage.removeItem(LSRefreshKey);
      return false;
    }
  }

  return false;
};

/**
 * See `completeAuthentication` for how this is used. Separated out to make
 * testing easy (see `Login.test.tsx` for how!)
 */
export const tokenRefresher = async (
  tokens: AuthSuccessSpec,
  setAuthentication: (auth: State<Authentication>) => void,
  authApiFlag?: boolean,
) => {
  try {
    const freshTokens = authApiFlag
      ? (
          await catalog.auth.refreshTokens({
            token: tokens.refreshToken,
            authRequestConfig: {},
          })
        ).data
      : await authService.refreshToken(tokens.refreshToken);

    localStorage.setItem(LSRefreshKey, freshTokens.refreshToken);
    setAuthentication({
      status: "success",
      error: null,
      data: {
        ...freshTokens,
        ...jwtDecode<JWT>(freshTokens.identityToken),
      },
    });
  } catch (error) {
    localStorage.removeItem(LSRefreshKey);
    location.assign(tokens.logoutUrl);
  }
};

/**
 * A small helper to complete a *successful* authentication flow. Updates the
 * Refresh Token in LocalStorage, calls the hook that allows for the app's
 * children to mount (i.e., clears the loading state) and sets up a function
 * that refreshes the ID token regularly.
 */
export const completeAuthentication = (
  tokens: AuthSuccessSpec,
  setAuthentication: (auth: State<Authentication>) => void,
  authApiFlag?: boolean,
) => {
  localStorage.setItem(LSRefreshKey, tokens.refreshToken);

  setAuthentication({
    status: "success",
    error: null,
    data: {
      ...tokens,
      ...jwtDecode<JWT>(tokens.identityToken),
    },
  });

  /**
   * We fetch a new set of tokens every 50 minutes. As of this writing, the ID
   * Token (which we need to call all upstream services) expires after 60
   * minutes but the Refresh Token is valid for a year (yes, you read that
   * right.) So every 50 minutes, we just call the Auth Service to exchange the
   * Refresh Token for a fresh set of tokens ✨
   *
   * Why 50 minutes? Nothing magical: it's just a reasonable number that's
   * smaller than 60 minutes. That's it! You could certainly set a more
   * aggressive interval (we used to refresh every FIVE minutes) but... why?
   */
  setInterval(
    tokenRefresher,
    refreshInterval,

    /**
     * Arguments! Did you know you could do this with `setInterval` (instead of
     * passing arguments to the target function via an anonymous function!)
     */
    tokens,
    setAuthentication,
    authApiFlag,
  );
};

/**
 *
 * --- Login Component ---
 *
 */

const Login: React.FC<ShellProps> = (props) => {
  React.useEffect(() => {
    console.debug("[@granular/fabric-core][Login] mounted");
  }, []);

  const authApiFlag = useMaybeFabricFeatureFlag({
    key: "auth-api",
    ignoreOperationId: true,
  });

  const setAuthentication = useSetAuthentication();

  const granularId = useMaybeUserId();
  const locale = useMaybeLocale();

  const { data: auth, status: authStatus } = useMaybeAuthentication();
  const { data: user, status: userStatus } = useMaybeUser();
  const { data: operations, status: operationsStatus } =
    useMaybePreOperations();

  useManageEnablingFreshData();

  // Use fabric feature flags for the current user but not
  // for the current operation (since that won't be known until CurrentOperation.tsx)
  const fabricFlagsStatus = useFabricFeatureFlagsStatus({
    ignoreOperationId: true,
  });

  useManageLocaleHeader();

  /**
   * This is the effect that attempts auth.
   */
  React.useEffect(() => {
    // Do not run this logic if we already have started attempting
    // authentication or have already attempted authentication
    if (authStatus !== "idle") {
      return;
    }

    console.debug("[@granular/fabric-core][Login] Attempting authentication");

    setAuthentication({
      status: "loading",
      error: null,
      data: undefined,
    });

    /**
     * --- FOR QA'S ROBOTS 🤖 ---
     *
     * This is to let QA test a Fabric App or Reporting on the server.
     *
     * If we see an Identity Token present in
     * LocalStorage (which they inject before running their tests) authenticate.
     * You're done 🏁
     *
     * NOTE: There is no validity check here! It is assumed that the ID Token
     * being injected is valid. Speeds things up, you know?
     *
     * TODO: Maybe extract into a function?
     *
     *      if(!tryBeingARobot()) {...}
     *
     * Might be easier to test 🤷‍♂️
     *
     */
    const LSRIdentityToken = localStorage.getItem(LSIdentityKey);
    if (navigator.webdriver && LSRIdentityToken) {
      const myJWT = jwtDecode<JWT>(LSRIdentityToken);

      setAuthentication({
        status: "success",
        error: null,
        data: {
          user: myJWT.email || "",
          identityToken: LSRIdentityToken,
          accessToken: "",
          refreshToken: "",
          logoutUrl: "",
          granularId: myJWT["custom:granular_id"] || "",
          ...myJWT,
        },
      });
    } else {
      /**
       * --- FOR HUMAN BEINGS 💁‍♀️ ---
       *
       * Basic outline in simple language:
       *
       * - Check for Session Code
       * - If it doesn't exist or is crappy, check for cached Refresh Token
       * - If it doesn't exist or is crappy, take them to SSO
       *
       * That's really it. Please read the function docstrings for more info.
       *
       */
      (async () => {
        let tokens = await tryLocalRefreshToken(authApiFlag);

        if (tokens) {
          completeAuthentication(tokens, setAuthentication, authApiFlag);
          return;
        }

        tokens = await trySessionCode(authApiFlag);
        if (tokens) {
          completeAuthentication(tokens, setAuthentication, authApiFlag);
          return;
        }

        localStorage.removeItem(LSRefreshKey);
        location.assign(
          `${getSSOUrl()}?url=${encodeURIComponent(location.href)}`,
        );
      })();
    }
  }, [setAuthentication, authStatus, authApiFlag]);

  React.useEffect(() => {
    if (granularId) {
      segmentService.identify(granularId);

      /**
       * This is DataDog (DD)'s Real User Monitoring (RUM) service. That's what
       * `DD_RUM` means.
       */
      const { DD_RUM } = window as any;

      if (DD_RUM?.setUser) {
        DD_RUM.setUser({
          id: granularId,
        });
      }
    }
  }, [granularId]);

  const isError = React.useMemo(
    () =>
      authStatus === "error" ||
      userStatus === "error" ||
      operationsStatus === "error" ||
      fabricFlagsStatus === "error",
    [authStatus, userStatus, operationsStatus, fabricFlagsStatus],
  );

  const isReady = React.useMemo(
    () =>
      auth &&
      user &&
      granularId &&
      locale &&
      operations &&
      fabricFlagsStatus === "success",
    [auth, user, granularId, locale, operations, fabricFlagsStatus],
  );

  if (isError) {
    console.error("[@granular/fabric-core][Login] Error", {
      authStatus,
      userStatus,
      operationsStatus,
      fabricFlagsStatus,
    });

    return <BigGiantOops />;
  }

  if (!isReady) {
    console.debug("[@granular/fabric-core][Login] Loading", {
      auth,
      user,
      granularId,
      locale,
      operations,
      fabricFlagsStatus,
    });

    // Because this is rendering some UI, we need GKLayoutShell
    return (
      <GKLayoutShell>
        <Loading />
      </GKLayoutShell>
    );
  }

  return <AddOperation {...props} />;
};

export default Login;
